import { useEffect, useRef, useState, useId } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import settingsService from '../../services/settingsService';

/**
 * DropdownCombobox
 * ----------------
 * Editierbares Eingabefeld mit sichtbarer Vorschlagsliste (Popup).
 * Nutzer können frei tippen ODER per Chevron-Klick eine Auswahl treffen.
 * Vorschlagswerte kommen aus einer Dropdown-Kategorie
 * (Einstellungen → Dropdown-Werte).
 *
 * Props:
 *   value           : string
 *   onChange        : (val: string) => void
 *   category        : string – z. B. "position_function", "appointment_title"
 *   placeholder     : string
 *   disabled        : boolean
 *   className       : string   (wird auf das Input angewendet)
 *   'data-testid'   : string
 */
export default function DropdownCombobox({
  value,
  onChange,
  category,
  placeholder,
  disabled = false,
  className = '',
  'data-testid': testid,
  ...rest
}) {
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const uid = useId();
  const listboxId = `combobox-list-${uid}`;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await settingsService.getDropdownValues(category);
        if (!alive) return;
        setOptions(data?.values || []);
      } catch (_e) {
        // Silent – Feld bleibt trotzdem als freies Textfeld nutzbar
      }
    })();
    return () => { alive = false; };
  }, [category]);

  // Klick außerhalb schließt das Popup
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const q = (value || '').toLowerCase();
  const filtered = q
    ? options.filter((o) => (o.label || o.value || '').toLowerCase().includes(q))
    : options;

  const pick = (label) => {
    onChange?.(label);
    setOpen(false);
  };

  // Basis-Input-Klassen (fallback für Fälle ohne className)
  const inputClass =
    className ||
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => { onChange?.(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`${inputClass} pr-9`}
          data-testid={testid}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          autoComplete="off"
          {...rest}
        />
        <button
          type="button"
          onClick={() => !disabled && setOpen((o) => !o)}
          tabIndex={-1}
          className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-500 hover:text-gray-700"
          aria-label="Vorschläge öffnen"
          data-testid={testid ? `${testid}-toggle` : undefined}
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-[80] mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
          data-testid={testid ? `${testid}-options` : undefined}
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500 italic">
              {options.length === 0
                ? 'Keine Einträge in den Einstellungen — freie Eingabe möglich.'
                : 'Kein Treffer — freie Eingabe möglich.'}
            </div>
          ) : (
            filtered.map((o) => {
              const label = o.label || o.value || '';
              const selected = label === value;
              return (
                <button
                  key={o.id || label}
                  type="button"
                  onClick={() => pick(label)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
                    selected ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50 text-gray-800'
                  }`}
                  role="option"
                  aria-selected={selected}
                >
                  {selected ? <Check className="w-3.5 h-3.5 text-blue-600" /> : <span className="w-3.5 h-3.5" />}
                  <span className="truncate">{label}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
